<template>
  <div class="AtlasHeader">
    <h1
      class="AtlasHeader-title"
      v-if="displayHeaderTitle && (indicatorConfig.title || geoText)"
    >
      <span>{{ indicatorConfig.title }}</span>
      <atlas-icon
        id="tour-help"
        class="AtlasHeader-helpIcon"
        name="help-circle"
        :class="{ 'is-active': isModalActive }"
        :size="16"
        @click="toggleModal"
        ref="modalTrigger"
      />
      <span v-if="geoText">{{ geoText }}</span>
    </h1>

    <div
      v-show="isModalActive"
      v-click-outside="onClickOutsideModal"
      class="AtlasHeader-modal"
    >
      <strong v-if="indicatorConfig.subhed">
        {{ indicatorConfig.subhed }}&nbsp;
      </strong>
      <span
        v-if="indicatorConfig.description"
        v-html="sanitize(indicatorConfig.description)"
      ></span>
      <span v-if="indicatorConfig.modalLink">
        |
        <a :href="indicatorConfig.modalLink">
          {{ indicatorConfig.modalLinkText }}
        </a>
      </span>
    </div>
    <div
      class="AtlasHeader-context"
      :style="isDataSummarySkin ? 'padding-left: 0; padding-right:0' : ''"
      v-if="contextText"
      v-html="sanitize(contextText)"
    ></div>
  </div>
</template>

<script>
import sanitize from 'sanitize-html';
import { uniq } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { SKIN_OPTIONS, SKIN_KEYS } from '../../constants';

const decipherYear = (year, y2k = true) => {
  // Basic pattern: `8015` means 1980–2015. `00015` means 2000-2015.

  // If the year starts with 19 or 20, assume it doesn't need to be decoded
  if (/^(19|20)/.test(year) || !y2k) return year;

  // Assume 4 digits, representing decade/year 1 & decade/year 2
  const [d1, y1, d2, y2] = year;
  // if first digit is > 3, assume century is 1900's. Always assume second
  // part of range is in the 21st century.
  return `${d1 >= 3 ? '19' : '20'}${d1}${y1}–20${d2}${y2}`;
};

export default {
  name: 'atlas-widget-header',

  props: {
    indicatorConfig: {
      type: Object,
      required: true,
    },

    breakdownId: String,

    skin: String,
  },

  data: () => ({
    isModalActive: false,
  }),

  computed: {
    ...mapState('indicator', ['indicator', 'indicatorId']),

    ...mapState('breakdown', ['primaryGeo', 'secondaryGeo']),

    ...mapGetters('display', ['displayHeaderTitle']),

    ...mapGetters('breakdown', [
      'translationsByBreakdownId',
      'breakdownById',
      'contextByBreakdownId',
      'dataByBreakdownId',
    ]),

    breakdown() {
      return this.breakdownById(this.breakdownId);
    },
    context() {
      return this.contextByBreakdownId(this.breakdownId);
    },
    translations() {
      return this.translationsByBreakdownId(this.breakdownId);
    },
    data() {
      return this.dataByBreakdownId(this.breakdownId);
    },

    yearRange() {
      const years = this.breakdown.queryFilters.year || [];

      // If there are no years to display we're done
      if (!years.length) return '';

      // Get the actual labels from all filters and build a dicitonary
      const yearLabels = years.reduce(
        (acc, y) => ({
          ...acc,
          // decipherYear is needed on maps using a single year range query
          // filter. This is because the map data query is not expanded for
          // performance reasons.
          [y]:
            this.translations[`year:${y}`] ||
            decipherYear(y, this.indicatorId < 191 /* gitlab issue #198 */),
        }),
        {},
      );

      // If there's only 1 year attempt to return the label/description and
      // fallback to just the key itself
      if (years.length === 1) return yearLabels[years[0]] || years[0];

      // If they're all single years, the values will all equal the keys
      // and we should return a range
      const isRange = years.every((y) => y === yearLabels[y]);
      if (isRange) return this.$d3.extent(years).join('–');
      // Otherwise return a comma-separated list of the labels
      return years.map((y) => yearLabels[y]).join(', ');
    },

    isDataSummarySkin() {
      return this.skin === SKIN_KEYS.DATA_SUMMARY;
    },

    filterTexts() {
      const contextEntries = Object.entries(
        this.context,
      ).map(([key, value]) => [
        this.isDataSummarySkin ? '' : key,
        `${key}:${value}`,
      ]);

      return contextEntries.map((keys) =>
        keys
          .filter((k) => this.translations[k])
          // Filters without ':' in the key are labels (bold)
          .map((k) => {
            return k.indexOf(':') >= 0
              ? this.translations[k]
              : this.translations[k] &&
                  `<strong>${this.translations[k]}:</strong>`;
          })
          .join(' '),
      );
    },

    contextLabels() {
      return [
        this.geoText,
        ...this.filterTexts,
        this.yearRange && !this.context.year ? this.yearRange : undefined,
      ].filter((x) => x);
    },

    contextText() {
      // If you want to add indicator or data summary text in
      // widget display, just add skin in return
      let skin = '';
      if (this.skin !== SKIN_KEYS.DATA_SUMMARY) {
        skin = SKIN_OPTIONS.find((option) => option.key === this.skin)?.text;
      }

      return [this.breakdown.description, this.contextLabels.join('; ')]
        .filter((x) => x)
        .join(': ');
    },

    geoText() {
      // If geo text is displayed in the header title, don't display in the
      // description.
      return uniq(
        [this.primaryGeo, this.secondaryGeo]
          .map((geo) => geo.text)
          .filter((x) => x),
      ).join(' vs. ');
    },
  },

  methods: {
    sanitize,

    toggleModal() {
      this.isModalActive = !this.isModalActive;
    },

    onClickOutsideModal(event) {
      if (!this.isModalActive) return;

      const { modalTrigger: trigger } = this.$refs;
      const { target } = event;
      if (!trigger || !trigger.$el.contains(target)) this.isModalActive = false;
    },
  },
};
</script>

<style lang="scss">
.AtlasHeader {
  &-title {
    padding: 8px $gutter 6px;
    border-bottom: solid 2px color(white);
    margin: 0;
    font-size: 24px;
    font-weight: bold;
    line-height: 30px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &-helpIcon {
    margin: 0 0.25em;
    color: color(shadow);
    cursor: pointer;

    &:hover,
    &.is-active {
      color: color(black);
    }
  }

  &-modal {
    @include elevation;
    position: absolute;
    z-index: 20;
    width: 600px;
    padding: 15px $gutter 20px;
    background-color: color(bg);
    font-size: 15px;
    line-height: 20px;
  }

  &-context {
    padding: 10px $gutter 11px;
    // https://gitlab.com/policy-link/data_atlas/-/issues/156
    // font-size: 24px;
    font-size: 18px;
    line-height: 30px;
  }
}
</style>
